import type { CropExportTarget } from "@/components/image-crop/exportCroppedImage";
import {
  formatAspectRatioLabel,
  type PlacementCatalogEntry,
} from "../../placementCatalog";

/**
 * Library images live on blob storage that doesn't send CORS headers yet, so
 * the browser can't read their pixels to crop them. Flip to true once the
 * storage CORS rule is in place; nothing else needs to change. (A file the
 * operator has just picked can always be cropped — it never leaves the browser.)
 */
export const CAN_CROP_LIBRARY_IMAGES = false;

/** A placement, or a shape several placements share. */
export type CropPlacement = Pick<
  PlacementCatalogEntry,
  "name" | "aspectRatio" | "width" | "height"
>;

const MAX_NAME_LENGTH = 100;
/** Used only when metadata has a ratio but no pixel size. */
const FALLBACK_WIDTH = 1000;

/** "Ridgeline Brewing (2:1)", trimmed so the suffix survives the 100-char limit. */
export function croppedImageName(
  sourceName: string,
  aspectRatio: number,
): string {
  const suffix = ` (${formatAspectRatioLabel(aspectRatio)})`;
  const base = sourceName.replace(/\.[^./\\]+$/, "").trim() || "Image";
  return `${base.slice(0, MAX_NAME_LENGTH - suffix.length)}${suffix}`;
}

/** Exact metadata size; PNG sources stay PNG so transparency survives. */
export function cropExportTarget(
  placement: CropPlacement,
  sourceType: string,
): CropExportTarget {
  const width = placement.width ?? FALLBACK_WIDTH;
  const height =
    placement.height ?? Math.round(width / (placement.aspectRatio || 1));
  return sourceType === "image/jpeg"
    ? { width, height, type: "image/jpeg", quality: 0.92 }
    : { width, height, type: "image/png" };
}

/** "Locked to 2:1 · Saves as Ridgeline (2:1), sized 1000 × 500 for Hole Load" */
export function cropDescription(
  sourceName: string,
  placement: CropPlacement,
  sourceType: string,
): string {
  const target = cropExportTarget(placement, sourceType);
  const ratio = formatAspectRatioLabel(placement.aspectRatio);
  return `Locked to ${ratio} · Saves as ${croppedImageName(sourceName, placement.aspectRatio)}, sized ${target.width} × ${target.height} for ${placement.name}`;
}
